import React, { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Phone, ClipboardCheck, MapPin, ChevronRight, ShieldCheck, Clock, Star } from "lucide-react";
import { getLocation, getRelated } from "../lib/locations";
import { SERVICES, PHONE_DISPLAY, PHONE_TEL, AREAS_IMG } from "../lib/site";
import { Services } from "../components/site/Services";
import { WhyChooseUs } from "../components/site/WhyChooseUs";
import { ContactForm } from "../components/site/ContactForm";

export default function ServiceArea() {
  const { slug } = useParams();
  const loc = getLocation(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (loc) {
      const title =
        loc.type === "state"
          ? `Handyman & Home Services in ${loc.name} | Journey's Handyman`
          : `Handyman & Restoration in ${loc.label} | Journey's Handyman`;
      document.title = title;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) {
        desc.setAttribute(
          "content",
          `Journey's Handyman Services — local handyman, remodeling and 24/7 emergency restoration serving ${loc.label}. Painting, decks, roofing, flooring, cabinets, remodels and more. Free estimates — call ${PHONE_DISPLAY}.`
        );
      }
    }
    return () => {
      document.title =
        "Blairsville Handyman Services & Restoration | Journey's Handyman — North GA";
    };
  }, [slug, loc]);

  if (!loc) return <Navigate to="/" replace />;

  const related = getRelated(loc);
  const isState = loc.type === "state";
  const headline = isState
    ? `Handyman & Home Services in ${loc.name}`
    : `Handyman & Restoration in ${loc.label}`;

  return (
    <div data-testid="service-area-page">
      {/* Banner */}
      <section className="relative pt-20 sm:pt-24">
        <div className="absolute inset-0">
          <img src={AREAS_IMG} alt={`North Georgia mountains near ${loc.label}`} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#0A1E3F]/88" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <nav className="flex items-center gap-2 text-white/60 text-sm font-heading uppercase tracking-wide mb-6">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-[#C8102E]">Service Areas</span>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">{loc.label}</span>
          </nav>

          <div className="inline-flex items-center gap-2 bg-[#C8102E] text-white font-heading font-bold uppercase tracking-[0.2em] text-xs sm:text-sm px-4 py-2 mb-6">
            <MapPin className="h-4 w-4" strokeWidth={3} /> {loc.stateName}
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tight leading-[0.95] text-white max-w-4xl">
            {headline}
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-white/85 font-medium max-w-3xl leading-relaxed">
            Journey's Handyman Services is your trusted local crew for home repairs, remodels
            and 24/7 emergency restoration in {loc.label}. From painting and decks to flooring,
            cabinets, roofing and water damage repair — one call does it all across {loc.stateName}.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <a href={`tel:${PHONE_TEL}`} data-testid="area-call-button" className="inline-flex items-center justify-center gap-3 bg-[#C8102E] hover:bg-[#9E0C24] text-white font-heading font-bold uppercase tracking-wide text-lg px-8 py-4 shadow-hard-sm hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-all">
              <Phone className="h-6 w-6" strokeWidth={2.5} /> Call {PHONE_DISPLAY}
            </a>
            <a href="#quote" data-testid="area-quote-button" className="inline-flex items-center justify-center gap-3 bg-white hover:bg-[#F6F5F0] text-[#0E2A56] font-heading font-bold uppercase tracking-wide text-lg px-8 py-4 transition-colors">
              <ClipboardCheck className="h-6 w-6" strokeWidth={2.5} /> Free Quote
            </a>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-white/90 font-semibold">
            <span className="flex items-center gap-2"><Clock className="h-5 w-5 text-[#C8102E]" strokeWidth={2.5} /> Same-Day & 24/7</span>
            <span className="flex items-center gap-2"><Star className="h-5 w-5 text-[#F6B100] fill-current" /> Trusted Locally</span>
          </div>
        </div>
      </section>

      {/* Local intro */}
      <section className="py-16 md:py-20 bg-[#F6F5F0]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-[#0E2A56]">
            Your Local Handyman in {loc.name}
          </h2>
          <p className="mt-4 text-lg text-slate-700 leading-relaxed">
            Based in Blairsville, GA and proudly serving {loc.label} within our 50-mile service
            area, Journey's Handyman Services brings the workmanship of a contractor and the
            friendliness of a neighbor to every job. Whether you need a quick repair, a full
            remodel, or emergency water and storm damage restoration, our mountain-trusted team
            is ready to help homeowners across {loc.name} and the surrounding {loc.stateName} area.
          </p>
          <p className="mt-4 text-lg text-slate-700 leading-relaxed">
            Call <a href={`tel:${PHONE_TEL}`} className="font-bold text-[#C8102E]">{PHONE_DISPLAY}</a> for
            fast, honest service and free estimates in {loc.label}.
          </p>
        </div>
      </section>

      <Services />
      <WhyChooseUs />

      {/* Nearby areas */}
      <section className="py-20 md:py-24 bg-[#F6F5F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-heading font-bold uppercase tracking-[0.2em] text-[#C8102E] text-sm">Also Serving</p>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-[#0E2A56]">
            Nearby Areas We Cover
          </h2>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/service-area/${r.slug}`}
                data-testid={`related-area-${r.slug}`}
                className="flex items-center gap-2 bg-white border-2 border-[#0E2A56] px-4 py-3 font-heading font-semibold uppercase tracking-wide text-[#0E2A56] text-sm hover:bg-[#0E2A56] hover:text-white transition-colors"
              >
                <MapPin className="h-4 w-4 text-[#C8102E] shrink-0" strokeWidth={3} />
                {r.label}
              </Link>
            ))}
          </div>
          <Link to="/#areas" className="mt-8 inline-flex items-center gap-2 font-heading font-bold uppercase tracking-wide text-[#0E2A56] hover:text-[#C8102E] transition-colors">
            View all service areas <ChevronRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      <ContactForm />
    </div>
  );
}
